import { ReactNode, useEffect, useState } from 'react';
import { ApolloCache, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import Bugsnag from '@bugsnag/js';
import { FlexBox, Spinner } from '@vp/swan';
import { APP_TENANT, useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { createBrowserApolloClient } from './apolloClient';
import { getGraphQLErrorType } from './getApolloErrorType';

export function AuthenticatedApolloProvider({
    locale,
    environment,
    children,
    endpoint,
    hideLoader,
    cache,
}: {
    locale: string;
    environment?: string;
    children: ReactNode;
    endpoint: string;
    hideLoader?: boolean;
    cache?: ApolloCache<NormalizedCacheObject>;
}) {
    const { auth } = useIdentityContext();
    const authorizationHeader = auth?.getAuthorizationHeader();
    const authHeader = authorizationHeader ? { Authorization: authorizationHeader } : undefined;

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authorizationHeader) {
            setLoading(false);
        }
    }, [authorizationHeader]);

    if (loading && !hideLoader) {
        return (
            <FlexBox justifyContent="center" marginBottom={'7'}>
                <Spinner size="super" accessibleText={__('Loading…')} />
            </FlexBox>
        );
    }

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        const operationName = operation.operationName || 'anonymous';
        if (graphQLErrors)
            graphQLErrors.forEach(({ extensions }) => {
                const errorType = getGraphQLErrorType(extensions?.type);
                if (!['PERMISSION_DENIED', 'NOT_FOUND', 'UNAUTHENTICATED'].includes(errorType)) {
                    Bugsnag.notify(new Error(`GraphQL request failed (${errorType})`), (event) => {
                        event.groupingHash = `graphql:${operationName}:${errorType}`;
                        event.addMetadata('GraphQL', {
                            errorType,
                            operationName,
                        });
                    });
                }
            });
        if (networkError) {
            Bugsnag.notify(new Error('GraphQL network request failed'), (event) => {
                event.groupingHash = `graphql:${operationName}:network`;
                event.addMetadata('GraphQL', {
                    errorType: 'NETWORK',
                    operationName,
                });
            });
        }
    });

    const apolloClient = createBrowserApolloClient(
        endpoint,
        {
            headers: {
                locale,
                Environment: environment ?? (process.env.NODE_ENV || 'development'),
                tenant: APP_TENANT,
                ...authHeader,
            },
        },
        errorLink,
        cache
    );

    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
